import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { monitoringService } from "@/services/monitoringService";
import {
  CreateServerDto,
  UpdateServerDto,
  TimeRange,
} from "@/types/monitoring";

export const monitoringKeys = {
  all: ["monitoring"] as const,
  servers: () => [...monitoringKeys.all, "servers"] as const,
  server: (id: string) => [...monitoringKeys.servers(), id] as const,
  health: (id: string) => [...monitoringKeys.all, "health", id] as const,
  cpuCurrent: (id: string) =>
    [...monitoringKeys.all, "cpu", "current", id] as const,
  cpuHistory: (id: string, from: string, to: string) =>
    [...monitoringKeys.all, "cpu", "history", id, from, to] as const,
  memoryCurrent: (id: string) =>
    [...monitoringKeys.all, "memory", "current", id] as const,
  memoryHistory: (id: string, from: string, to: string) =>
    [...monitoringKeys.all, "memory", "history", id, from, to] as const,
  containerCpu: (
    serverId: string,
    containerId: string,
    from: string,
    to: string,
  ) =>
    [
      ...monitoringKeys.all,
      "container",
      "cpu",
      serverId,
      containerId,
      from,
      to,
    ] as const,
  containerMemory: (
    serverId: string,
    containerId: string,
    from: string,
    to: string,
  ) =>
    [
      ...monitoringKeys.all,
      "container",
      "memory",
      serverId,
      containerId,
      from,
      to,
    ] as const,
};

/**
 * Rounds timestamps to the nearest minute to produce stable query keys.
 * Without rounding, every render creates a unique ISO string → infinite cache growth.
 */
function getTimeRangeDates(range: TimeRange): { from: string; to: string } {
  const now = new Date();
  now.setSeconds(0, 0); // round to minute
  const to = now.toISOString();
  const ms: Record<TimeRange, number> = {
    "1h": 60 * 60 * 1000,
    "6h": 6 * 60 * 60 * 1000,
    "24h": 24 * 60 * 60 * 1000,
    "7d": 7 * 24 * 60 * 60 * 1000,
  };
  const from = new Date(now.getTime() - ms[range]).toISOString();
  return { from, to };
}

// --- Server CRUD ---

export const useMonitoringServers = () => {
  return useQuery({
    queryKey: monitoringKeys.servers(),
    queryFn: () => monitoringService.getServers(),
    staleTime: 5 * 60 * 1000,
  });
};

export const useMonitoringServer = (id: string) => {
  return useQuery({
    queryKey: monitoringKeys.server(id),
    queryFn: () => monitoringService.getServer(id),
    staleTime: 5 * 60 * 1000,
    enabled: !!id,
  });
};

export const useCreateServer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateServerDto) => monitoringService.createServer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: monitoringKeys.servers() });
    },
  });
};

export const useUpdateServer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateServerDto }) =>
      monitoringService.updateServer(id, data),
    onSuccess: (updated) => {
      queryClient.setQueryData(monitoringKeys.server(updated.id), updated);
      queryClient.invalidateQueries({ queryKey: monitoringKeys.servers() });
    },
  });
};

export const useDeleteServer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => monitoringService.deleteServer(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: monitoringKeys.server(id) });
      queryClient.invalidateQueries({ queryKey: monitoringKeys.servers() });
    },
  });
};

// --- Health ---

export const useServerHealth = (id: string, enabled = true) => {
  return useQuery({
    queryKey: monitoringKeys.health(id),
    queryFn: async () => {
      try {
        return await monitoringService.getServerHealth(id);
      } catch (error: unknown) {
        // Let auth errors (401) propagate to trigger token refresh
        if (
          typeof error === "object" &&
          error !== null &&
          "statusCode" in error &&
          (error as { statusCode: number }).statusCode === 401
        ) {
          throw error;
        }
        return { reachable: false };
      }
    },
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    enabled: !!id && enabled,
  });
};

// --- CPU ---

export const useCpuCurrent = (serverId: string) => {
  return useQuery({
    queryKey: monitoringKeys.cpuCurrent(serverId),
    queryFn: () => monitoringService.getCpuCurrent(serverId),
    staleTime: 0,
    refetchInterval: 10 * 1000,
    enabled: !!serverId,
  });
};

export const useCpuHistory = (serverId: string, range: TimeRange) => {
  const { from, to } = getTimeRangeDates(range);
  return useQuery({
    queryKey: monitoringKeys.cpuHistory(serverId, from, to),
    queryFn: () => monitoringService.getCpuHistory(serverId, from, to),
    staleTime: 30 * 1000,
    enabled: !!serverId,
  });
};

// --- Memory ---

export const useMemoryCurrent = (serverId: string) => {
  return useQuery({
    queryKey: monitoringKeys.memoryCurrent(serverId),
    queryFn: () => monitoringService.getMemoryCurrent(serverId),
    staleTime: 0,
    refetchInterval: 10 * 1000,
    enabled: !!serverId,
  });
};

export const useMemoryHistory = (serverId: string, range: TimeRange) => {
  const { from, to } = getTimeRangeDates(range);
  return useQuery({
    queryKey: monitoringKeys.memoryHistory(serverId, from, to),
    queryFn: () => monitoringService.getMemoryHistory(serverId, from, to),
    staleTime: 30 * 1000,
    enabled: !!serverId,
  });
};

// --- Containers ---

export const useContainerCpuHistory = (
  serverId: string,
  containerId: string,
  range: TimeRange,
) => {
  const { from, to } = getTimeRangeDates(range);
  return useQuery({
    queryKey: monitoringKeys.containerCpu(serverId, containerId, from, to),
    queryFn: () =>
      monitoringService.getContainerCpuHistory(serverId, containerId, from, to),
    staleTime: 30 * 1000,
    enabled: !!serverId && !!containerId,
  });
};

export const useContainerMemoryHistory = (
  serverId: string,
  containerId: string,
  range: TimeRange,
) => {
  const { from, to } = getTimeRangeDates(range);
  return useQuery({
    queryKey: monitoringKeys.containerMemory(serverId, containerId, from, to),
    queryFn: () =>
      monitoringService.getContainerMemoryHistory(
        serverId,
        containerId,
        from,
        to,
      ),
    staleTime: 30 * 1000,
    enabled: !!serverId && !!containerId,
  });
};
